import { useState } from 'react';
import { isAxiosError } from 'axios';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Loader2,
  LocateFixed,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { postCameraPtz, type PtzAction } from '@/api/ptz';
import { extractApiError } from '@/utils/apiError';

interface Props {
  cameraId: number | string;
  className?: string;
}

/**
 * Pan/tilt pad for EZVIZ cameras (TY1: 350°/55°).
 *
 * Each arrow tap is one POST — the backend runs a timed nudge (start,
 * ~400 ms, stop), so a lost connection can't leave the camera turning.
 * «К сохранённой позиции» drives back to the camera's preset slot.
 * Сохранение пресета убрано (AI89): прошивка EZVIZ его не даёт — см.
 * комментарий у кнопок ниже.
 */
export function PtzPad({ cameraId, className }: Props) {
  const { t } = useT();
  const [busy, setBusy] = useState<PtzAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async (action: PtzAction) => {
    if (busy) return;
    setBusy(action);
    setError(null);
    try {
      await postCameraPtz(cameraId, action);
    } catch (e) {
      setError(
        isAxiosError(e)
          ? extractApiError(e, t('equipment.ptz.error'))
          : t('equipment.ptz.error'),
      );
    } finally {
      setBusy(null);
    }
  };

  const arrow = (action: PtzAction, Icon: typeof ArrowUp, label: string) => (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="size-9 p-0"
      aria-label={label}
      title={label}
      disabled={busy !== null}
      onClick={() => void send(action)}
    >
      {busy === action
        ? <Loader2 className="size-4 animate-spin" />
        : <Icon className="size-4" />}
    </Button>
  );

  return (
    <div className={className}>
      <p className="mb-2 text-sm font-medium">{t('equipment.ptz.title')}</p>
      <div className="flex items-center gap-4">
        <div className="grid w-fit grid-cols-3 gap-1">
          <div />
          {arrow('up', ArrowUp, t('equipment.ptz.up'))}
          <div />
          {arrow('left', ArrowLeft, t('equipment.ptz.left'))}
          <div />
          {arrow('right', ArrowRight, t('equipment.ptz.right'))}
          <div />
          {arrow('down', ArrowDown, t('equipment.ptz.down'))}
          <div />
        </div>
        <div className="grid gap-1.5">
          {/* «Запомнить позицию» временно убрана (Алексей 21.09, AI89): кнопка
              есть, а сохранения нет — камеры отдают NET_DVR error 11 даже там,
              где api_port заведомо верный (проверено на Astra и Flux). Это
              ограничение прошивки EZVIZ: пресеты у них живут в облаке, а не в
              локальном SDK. Показывать кнопку, которая всегда падает, хуже, чем
              не показывать: оператор думает, что позиция сохранена. Вернуть,
              когда заработают пресеты по ONVIF (AI90). Кнопку «К сохранённой»
              оставляем: пресет мог быть выставлен из приложения EZVIZ. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy !== null}
            onClick={() => void send('goto')}
          >
            {busy === 'goto'
              ? <Loader2 className="size-3.5 animate-spin" />
              : <LocateFixed className="size-3.5" />}
            {t('equipment.ptz.gotoPosition')}
          </Button>
        </div>
      </div>
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </div>
  );
}
